import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Send, ChevronLeft, Users, Search } from 'lucide-react';

const WorkerChat = () => {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // list | colleagues | chat
  const [conversations, setConversations] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [search, setSearch] = useState('');
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const msgEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.getInternalChats();
      setConversations(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await api.getInternalChatMessages(convId);
      setMessages(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (!activeConv) return;
    fetchMessages(activeConv.id);
    pollRef.current = setInterval(() => fetchMessages(activeConv.id), 4000);
    return () => clearInterval(pollRef.current);
  }, [activeConv, fetchMessages]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadColleagues = async () => {
    try {
      const res = await api.getInternalChatColleagues();
      setColleagues(res.data);
      setView('colleagues');
    } catch {
      toast.error('Kunde inte hämta kollegor');
    }
  };

  const startDM = async (colleague) => {
    try {
      const res = await api.createInternalChat({
        title: colleague.name || colleague.email,
        participants: [colleague.id],
        is_group: false,
      });
      setActiveConv(res.data);
      setView('chat');
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte starta chatt');
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await api.sendInternalMessage(activeConv.id, { message: newMsg });
      setNewMsg('');
      fetchMessages(activeConv.id);
    } catch {
      toast.error('Kunde inte skicka');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredColleagues = colleagues.filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase())
  );

  // ============ LIST VIEW ============
  if (view === 'list') {
    return (
      <div className="space-y-3 pb-20" data-testid="worker-chat-list">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare size={20} className="text-[#E8613A]" /> Chatt
          </h1>
          <Button size="sm" onClick={loadColleagues} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-9 text-xs" data-testid="new-dm-btn">
            <Users size={14} className="mr-1.5" /> Ny chatt
          </Button>
        </div>

        {conversations.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-xl p-8 text-center">
            <MessageSquare size={32} className="text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Inga chattar än. Tryck på "Ny chatt" för att börja prata med en kollega.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map(conv => (
              <button key={conv.id} onClick={() => { setActiveConv(conv); setView('chat'); }}
                className="w-full bg-card border border-border rounded-xl p-3 text-left hover:border-[#E8613A]/30 transition-colors"
                data-testid={`conv-${conv.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-foreground truncate">{conv.title}</span>
                  {conv.unread_count > 0 && (
                    <span className="bg-[#E8613A] text-white text-[10px] px-2 py-0.5 rounded-full">{conv.unread_count}</span>
                  )}
                </div>
                {conv.is_group && <p className="text-[10px] text-muted-foreground mb-1">Grupp · {conv.participants?.length || 0} medlemmar</p>}
                {conv.last_message && (
                  <p className="text-xs text-muted-foreground truncate">{conv.last_message.sender_name}: {conv.last_message.message}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ============ COLLEAGUES PICKER ============
  if (view === 'colleagues') {
    return (
      <div className="space-y-3 pb-20" data-testid="worker-chat-colleagues">
        <button onClick={() => setView('list')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ChevronLeft size={12} /> Tillbaka
        </button>
        <h1 className="text-xl font-bold">Starta chatt</h1>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sök kollega..." className="pl-9" data-testid="colleague-search" />
        </div>
        <div className="space-y-2">
          {filteredColleagues.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Inga kollegor hittade</p>
          ) : filteredColleagues.map(c => (
            <button key={c.id} onClick={() => startDM(c)}
              className="w-full bg-card border border-border rounded-xl p-3 text-left hover:border-[#E8613A]/30 transition-colors flex items-center gap-3"
              data-testid={`colleague-${c.id}`}>
              <div className="w-10 h-10 bg-[#E8613A]/10 rounded-full flex items-center justify-center text-[#E8613A] font-bold text-sm flex-shrink-0">
                {(c.name || c.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{c.name || c.email}</p>
                <p className="text-xs text-muted-foreground">{c.role === 'admin' ? 'Admin' : c.role === 'worker' ? 'Arbetare' : c.role}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ============ CHAT VIEW ============
  return (
    <div className="flex flex-col h-[calc(100vh-180px)]" data-testid="worker-chat-view">
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
        <button onClick={() => { setView('list'); setActiveConv(null); }} className="text-muted-foreground hover:text-foreground">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold truncate">{activeConv.title}</h2>
          {activeConv.is_group && <p className="text-[10px] text-muted-foreground">Grupp · {activeConv.participants?.length || 0} medlemmar</p>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 px-1">
        {messages.map(msg => {
          const isMe = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${isMe ? 'bg-[#E8613A] text-white' : 'bg-muted text-foreground'}`}>
                {!isMe && <p className="text-[10px] font-bold mb-0.5 opacity-70">{msg.sender_name}</p>}
                <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                <p className={`text-[9px] mt-1 ${isMe ? 'text-white/60' : 'text-muted-foreground'}`}>{formatTime(msg.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={msgEndRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2 mt-3 pt-3 border-t border-border">
        <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Skriv meddelande..." className="flex-1" data-testid="worker-chat-input" />
        <Button type="submit" disabled={sending || !newMsg.trim()} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="worker-chat-send">
          <Send size={14} />
        </Button>
      </form>
    </div>
  );
};

export default WorkerChat;
